import { buttonVariants } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import type { DocsTocEntry } from './docsTypes'

/** Sticky page header plus padding; headings above this line count as passed. */
const TOC_READ_LINE_PX = 120

type DocsTableOfContentsProps = {
  toc: DocsTocEntry[]
  className?: string
}

function pickActiveAnchor(
  headings: DocsTocEntry[],
  elements: HTMLElement[],
  visible: Map<string, IntersectionObserverEntry>,
): string {
  const sortedVisible = [...visible.values()].sort(
    (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
  )

  if (sortedVisible.length > 0) {
    return sortedVisible[0].target.id
  }

  let current = headings[0]?.anchor ?? ''
  for (const element of elements) {
    if (element.getBoundingClientRect().top <= TOC_READ_LINE_PX) {
      current = element.id
    }
  }

  return current
}

export function DocsTableOfContents({ toc, className = '' }: DocsTableOfContentsProps) {
  const location = useLocation()
  const headings = useMemo(() => toc.filter((entry) => entry.level >= 2), [toc])
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null)

  useEffect(() => {
    if (headings.length === 0) {
      setActiveAnchor(null)
      return
    }

    const elements = headings
      .map((entry) => document.getElementById(entry.anchor))
      .filter((element): element is HTMLElement => element !== null)

    if (elements.length === 0) {
      setActiveAnchor(headings[0].anchor)
      return
    }

    const visible = new Map<string, IntersectionObserverEntry>()

    const updateActive = () => {
      setActiveAnchor(pickActiveAnchor(headings, elements, visible))
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry)
          } else {
            visible.delete(entry.target.id)
          }
        })
        updateActive()
      },
      { rootMargin: `-${TOC_READ_LINE_PX}px 0px -55% 0px`, threshold: 0 },
    )

    elements.forEach((element) => observer.observe(element))
    updateActive()

    return () => {
      observer.disconnect()
      visible.clear()
    }
  }, [headings])

  useEffect(() => {
    if (!location.hash) {
      return
    }

    const anchor = location.hash.slice(1)
    if (headings.some((entry) => entry.anchor === anchor)) {
      setActiveAnchor(anchor)
    }
  }, [location.hash, headings])

  if (headings.length === 0) {
    return null
  }

  return (
    <aside
      className={cn(
        'docs-toc hidden w-max shrink-0 rounded-lg bg-background p-5 shadow-lg xl:block',
        className,
      )}
    >
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">On this page</p>
      <nav aria-label="Table of contents">
        <ul className="space-y-2 text-sm">
          {headings.map((entry) => {
            const isActive = entry.anchor === activeAnchor

            return (
              <li
                key={entry.anchor}
                style={{ paddingLeft: `${Math.max(0, entry.level - 2) * 0.75}rem` }}
                className="leading-snug"
              >
                <a
                  href={`#${entry.anchor}`}
                  aria-current={isActive ? 'location' : undefined}
                  className={cn(
                    buttonVariants({ variant: 'ghost' }),
                    'docs-toc-link h-min p-0 pl-2',
                    isActive && 'docs-toc-link--active',
                  )}
                >
                  {entry.title}
                </a>
              </li>
            )
          })}
        </ul>
      </nav>
    </aside>
  )
}
